// Centre de notifications & tâches (WKF-013) — mutations.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'

import { jsonHeaders, NOTIF_BASE, TASKS_BASE, readError } from './useNotifications.shared'
import type {
    Notification,
    NotificationList,
    TaskCreatePayload,
    TaskUpdatePayload,
    TransferPayload,
} from './useNotifications.types'

const TASKS_PREFIX = ['tasks'] as const

type OptimisticPatch = Partial<Notification>
/** Contexte de rollback des mutations optimistes (snapshot des caches touchés). */
type SnapshotCtx = { snapshot: [readonly unknown[], unknown][] }

/** Caches réellement « listants » (liste keyset + calendrier) — les SEULS à patcher
 *  optimistiquement. On EXCLUT onscreen (file de toasts Instantané : la patcher/annuler
 *  re-jouerait des toasts déjà affichés et casserait le drainage at-most-once) ainsi
 *  que detail / unread-count / absences (autres formes). */
const isListingKey = (key: readonly unknown[]) =>
    key[0] === 'notifications' && (key[1] === 'list' || key[1] === 'calendar')

/** Caches à invalider par une mutation de notif/tâche : tout `notifications` SAUF
 *  (a) `onscreen` (file de toasts Instantané — drainage at-most-once, ne pas re-fetcher)
 *  et (b) `absences` (collègues / absences / couverture — INCHANGÉS par une action de
 *  notif ; les inclure re-fetchait `/me/colleagues`, monté en permanence dans la cloche,
 *  à CHAQUE markRead/treat/snooze/transfer — refetch réseau superflu très fréquent). */
const shouldInvalidateNotifKey = (key: readonly unknown[]) =>
    key[0] === 'notifications' && key[1] !== 'onscreen' && key[1] !== 'absences'

/** Mutations personnelles (cloche + page + fiche client). */
export function useNotificationMutations(entityId: string | null) {
    const qc = useQueryClient()
    const eid = entityId ?? ''

    // Invalidation ciblée : tout `notifications` SAUF onscreen ET absences (cf.
    // shouldInvalidateNotifKey — évite le refetch en boucle des collègues).
    const invalidate = () => {
        void qc.invalidateQueries({ predicate: (q) => shouldInvalidateNotifKey(q.queryKey) })
    }
    const invalidateTasks = () => {
        invalidate()
        void qc.invalidateQueries({ queryKey: TASKS_PREFIX })
    }

    // Patch OPTIMISTE d'une notification dans les caches LISTANTS (list keyset +
    // calendar). Rend la coche + le rayé et le report INSTANTANÉS (perf perçue,
    // zéro layout shift) ; l'invalidation au settle réconcilie (retrait des
    // clôturés/reportés des listes open_only). Renvoie un snapshot pour rollback.
    async function optimisticPatch(id: string, patch: OptimisticPatch) {
        await qc.cancelQueries({ predicate: (q) => isListingKey(q.queryKey) })
        const snapshot = qc.getQueriesData<NotificationList>({ predicate: (q) => isListingKey(q.queryKey) })
        qc.setQueriesData<NotificationList>({ predicate: (q) => isListingKey(q.queryKey) }, (old) => {
            if (!old || !Array.isArray((old as { items?: unknown }).items)) return old
            return { ...old, items: old.items.map((n) => (n.id === id ? { ...n, ...patch } : n)) }
        })
        return { snapshot }
    }

    function rollback(ctx: SnapshotCtx | undefined) {
        ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data))
    }

    async function post<T = unknown>(url: string, body?: unknown): Promise<T> {
        const resp = await apiFetch(url, {
            method: 'POST',
            headers: jsonHeaders(eid),
            body: body !== undefined ? JSON.stringify(body) : undefined,
        })
        if (!resp.ok) throw await readError(resp)
        return resp.status === 204 ? (undefined as T) : resp.json()
    }

    const markRead = useMutation<Notification, Error, string, SnapshotCtx>({
        mutationFn: (id) => post<Notification>(`${NOTIF_BASE}/${id}/mark-read`),
        onMutate: (id) => optimisticPatch(id, { status: 'READ' }),
        onError: (_e, _id, ctx) => rollback(ctx),
        onSettled: invalidate,
    })

    const markAllRead = useMutation<{ count: number }, Error, void>({
        mutationFn: () => post<{ count: number }>(`${NOTIF_BASE}/mark-all-read`),
        onSuccess: invalidate,
    })

    // Épuration : masque mes tâches terminées (bucket « Terminées récemment »).
    const dismissCompleted = useMutation<{ count: number }, Error, void>({
        mutationFn: () => post<{ count: number }>(`${NOTIF_BASE}/dismiss-completed`),
        onSuccess: invalidateTasks,
    })

    const treat = useMutation<Notification, Error, string, SnapshotCtx>({
        mutationFn: (id) => post<Notification>(`${NOTIF_BASE}/${id}/treat`),
        onMutate: (id) => optimisticPatch(id, { status: 'TREATED' }),
        onError: (_e, _id, ctx) => {
            rollback(ctx)
            invalidateTasks()
        },
        // Délai de grâce (retour PO) : l'optimistic garde la ligne EN PLACE, cochée
        // et rayée ; on diffère la réconciliation (~2,5 s) pour laisser VOIR la
        // complétion avant que le refetch open_only ne retire la ligne du widget.
        onSuccess: () => {
            setTimeout(invalidateTasks, 2500)
        },
    })

    const reopen = useMutation<Notification, Error, string, SnapshotCtx>({
        mutationFn: (id) => post<Notification>(`${NOTIF_BASE}/${id}/reopen`),
        onMutate: (id) => optimisticPatch(id, { status: 'READ' }),
        onError: (_e, _id, ctx) => rollback(ctx),
        onSettled: invalidateTasks,
    })

    const snooze = useMutation<Notification, Error, { id: string; snoozed_until: string }, SnapshotCtx>({
        mutationFn: ({ id, snoozed_until }) =>
            post<Notification>(`${NOTIF_BASE}/${id}/snooze`, { snoozed_until }),
        onMutate: ({ id }) => optimisticPatch(id, { status: 'SNOOZED' }),
        onError: (_e, _v, ctx) => rollback(ctx),
        onSettled: invalidateTasks,
    })

    const transfer = useMutation<{ id: string }, Error, { id: string; payload: TransferPayload }>({
        mutationFn: ({ id, payload }) => post<{ id: string }>(`${NOTIF_BASE}/${id}/transfer`, payload),
        onSuccess: invalidateTasks,
    })

    const createTask = useMutation<Notification, Error, TaskCreatePayload>({
        mutationFn: (payload) => post<Notification>(TASKS_BASE, payload),
        onSuccess: invalidateTasks,
    })

    const updateTask = useMutation<Notification, Error, { id: string; payload: TaskUpdatePayload }>({
        mutationFn: async ({ id, payload }) => {
            const resp = await apiFetch(`${TASKS_BASE}/${id}`, {
                method: 'PATCH',
                headers: jsonHeaders(eid),
                body: JSON.stringify(payload),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
        onSuccess: invalidateTasks,
    })

    const deleteTask = useMutation<void, Error, string>({
        mutationFn: async (id) => {
            const resp = await apiFetch(`${TASKS_BASE}/${id}`, {
                method: 'DELETE',
                headers: jsonHeaders(eid),
            })
            if (!resp.ok) throw await readError(resp)
        },
        onSuccess: invalidateTasks,
    })

    return {
        markRead,
        markAllRead,
        dismissCompleted,
        treat,
        reopen,
        snooze,
        transfer,
        createTask,
        updateTask,
        deleteTask,
    }
}

/** Mutations de masse (console admin, tasks:manage_all). */
export function useTaskAdminMutations(entityId: string | null) {
    const qc = useQueryClient()
    const eid = entityId ?? ''

    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: TASKS_PREFIX })
        // Idem : exclut onscreen + absences (une action de masse ne change ni la file de
        // toasts ni les collègues) — sinon re-drain onscreen et refetch /me/colleagues.
        void qc.invalidateQueries({ predicate: (q) => shouldInvalidateNotifKey(q.queryKey) })
    }

    async function post<T = unknown>(url: string, body: unknown): Promise<T> {
        const resp = await apiFetch(url, {
            method: 'POST',
            headers: jsonHeaders(eid),
            body: JSON.stringify(body),
        })
        if (!resp.ok) throw await readError(resp)
        return resp.json()
    }

    const reassign = useMutation<
        { count: number },
        Error,
        { notification_ids: string[]; recipient_id: string; reason?: string | null }
    >({
        mutationFn: (body) => post(`${TASKS_BASE}/admin/reassign`, body),
        onSuccess: invalidate,
    })

    const bulkSnooze = useMutation<
        { count: number },
        Error,
        { notification_ids: string[]; snoozed_until: string }
    >({
        mutationFn: (body) => post(`${TASKS_BASE}/admin/snooze`, body),
        onSuccess: invalidate,
    })

    // Report d'ÉCHÉANCE de masse : déplace la due_date (visible), ≠ bulkSnooze.
    const bulkReschedule = useMutation<
        { count: number },
        Error,
        { notification_ids: string[]; due_date: string }
    >({
        mutationFn: (body) => post(`${TASKS_BASE}/admin/reschedule`, body),
        onSuccess: invalidate,
    })

    const bulkDelete = useMutation<{ count: number }, Error, { notification_ids: string[] }>({
        mutationFn: (body) => post(`${TASKS_BASE}/admin/delete`, body),
        onSuccess: invalidate,
    })

    return { reassign, bulkSnooze, bulkReschedule, bulkDelete }
}
